import {
  isServiceNameInES,
  SERVICE_NAME_MAP,
  SERVICE_NAME_ORDER_MAP,
} from 'src/utils/service';
import { getUtcTime } from 'src/utils/time';

import { ServiceNameEnum } from '../types';

export interface ChartData {
  //name is service name in es
  [name: string]: {
    total?: number;
    timerange: { [time: string]: number };
  };
}

interface OutChartEle {
  name: string;
  series: (string | number)[][];
}

export function formatChartData(
  inChart: ChartData,
  nameMap: Map<string, string>,
  offset: string,
  format: string
) {
  if (!inChart) {
    return [];
  }
  const filterDataByNameMapKey: ChartData = {};
  Object.keys(inChart).forEach((key) => {
    if (nameMap.get(key) !== undefined) {
      filterDataByNameMapKey[key] = inChart[key];
    }
  });
  const outChart: OutChartEle[] = [];
  let keys = Object.keys(filterDataByNameMapKey);
  if (keys.length === 0) {
    return outChart;
  }
  if (isServiceNameInES(keys[0])) {
    keys = keys.sort((s1, s2) => {
      return (
        SERVICE_NAME_ORDER_MAP[SERVICE_NAME_MAP.get(s1) as ServiceNameEnum] -
        SERVICE_NAME_ORDER_MAP[SERVICE_NAME_MAP.get(s2) as ServiceNameEnum]
      );
    });
  }

  for (const k of keys) {
    let nameI18n = nameMap.get(k);
    if (nameI18n === undefined) {
      nameI18n = k;
      console.error(`Unexpected value: ${k}, can't be parse`);
    }
    outChart.push({
      name: nameI18n,
      series: Object.entries(filterDataByNameMapKey?.[k]?.timerange || '')
        .map(([date, val]) => [getUtcTime(date, offset, format), val])
        .reverse(),
    });
  }

  return outChart;
}

export function surroundWildcardForAscii(str: string, field: string): string[] {
  const values: string[] = [];
  const rawStr = str;
  if (str.length === 0) {
    return values;
  }
  const isFirstCharUnicode = str.charCodeAt(0) > 127;
  const isLastCharUnicode = str.charCodeAt(str.length - 1) > 127;

  /**
   * seg: https://segjira.trendmicro.com/browse/SEG-157438
   * for -, it will skip tm_message_id & tm_internet_message_id
   * @param field
   * @param str
   * @returns {*[]}
   */
  const messageIdFields = ['messageId', 'internetMessageId'];
  if (
    isFirstCharUnicode === false &&
    str.indexOf(' ') === -1 &&
    (str.indexOf('-') === -1 ||
      (str.indexOf('-') > -1 && messageIdFields.includes(field)))
  ) {
    str = '*' + str;
  }
  if (isLastCharUnicode === false) {
    str = str + '*';
  }

  // query string has unicode-part then push raw string
  if (isFirstCharUnicode || isLastCharUnicode) {
    values.push(rawStr);
  }
  values.push(str);
  return values;
}
const removeSpecialCharAtQueryString = (string: string): string => {
  const specialCharacters = [
    '+',
    '-',
    '=',
    '&',
    '|',
    '>',
    '<',
    '!',
    '(',
    ')',
    '{',
    '}',
    '[',
    ']',
    '^',
    '"',
    '~',
    '*',
    '?',
    ':',
    '\\',
    '/',
    '@',
  ];

  if (specialCharacters.indexOf(string.substring(0, 1)) > -1) {
    string = string.substring(1);
  }

  if (
    ['"', ']', '}', ')', '>'].indexOf(
      string.substring(string.length - 1, string.length)
    ) > -1
  ) {
    string = string.substring(0, string.length - 1);
  }

  return string.trim();
};
export function escapeQueryString(field: string, string: string): string {
  // FYI: https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-query-string-query.html
  if (string && typeof string === 'string') {
    string = removeSpecialCharAtQueryString(string);
    if (field === 'internetMessageId' || field === 'messageId') {
      string = string
        .replace(/[><]/g, '')
        .replace(/[*+\-=~"?^${}()!/[\]\\]|\|\||&&/g, '\\$&')
        .replace(/AND/g, '\\A\\N\\D')
        .replace(/OR/g, '\\O\\R')
        .replace(/NOT/g, '\\N\\O\\T');
    } else {
      string = string
        .replace(/[><@:,]/g, ' ')
        .replace(/[*+\-=~"?^${}()!/[\]\\]|\|\||&&/g, '\\$&')
        .replace(/AND/g, '\\A\\N\\D')
        .replace(/OR/g, '\\O\\R')
        .replace(/NOT/g, '\\N\\O\\T');
    }
  }
  return string;
}
